import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { filter, get, isEmpty, size, some } from "lodash";
import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { FiAlertTriangle } from "react-icons/fi";
import { connect, type ConnectedProps } from "react-redux";
import { toast } from "react-toastify";

import { hydrateDashboard } from "actions/employerActions";
import { setTab } from "actions/navigationActions";
import { bankConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";
import {
  banksSelector,
  invalidBankIdsLinkedToGroupsSelector,
  isBankPending,
  isBankVerified,
  isBankVerifiedSelector,
} from "store/selectors/bank";
import { getPayrollIntegrationStatus } from "store/selectors/employer";
import { PendingDebitAuthorizationAcknowledgment } from "statics/payrollIntegrationStates";
import { isEmployerSelector } from "store/selectors/user";

import Button from "components/Button";
import IconBanner from "components/IconBanner";
import IconSpinner from "components/IconSpinner";
import PageHeading from "components/PageHeading";
import ReAuthPlaidLink from "components/ReAuthPlaidLink";
import AddBankCard from "pages/dashboards/AddBankCard";
import CurrentPayrollTable from "./CurrentPayrollTable";
import EmployeePlanParticipation from "./EmployeePlanParticipation";
import PayrollIntegratedACHDisclaimer from "./PayrollIntegratedACHDisclaimer";
import TotalsTable from "./TotalsTable";
import UpdateGroupCard from "./UpdateGroupCard";

import "./EmployerDashboard.scss";

import type { RootState } from "types/state";
import type { UserBank } from "types/models";

const errorSelector = createErrorSelector([
  bankConstants.OAUTH_PLAID_LINK_TOKEN,
]);

const mapStateToProps = (state: RootState) => {
  const pendingLinks = filter(
    state.employer.employerLinkRequests,
    (link) => (link as { status?: string }).status === "REQUEST_ACCESS"
  );

  const unacceptedInvites = filter(
    state.employer.employeeInvitations,
    (invite) => {
      return (invite as { status?: string }).status !== "ACCEPTED";
    }
  );
  const company = state.employer.company;

  const totalPlanParticipants = state.employer.linkedEmployees.length;
  const allBanks = banksSelector(state);
  const banksNeedingReAuth = allBanks.filter((b) => b.loginRequired);
  const hasMultipleBanksPendingReAuth = size(banksNeedingReAuth) > 1;
  const reAuthRequired = !isEmpty(banksNeedingReAuth);
  const reAuthBank = banksNeedingReAuth[0] ?? null;

  return {
    companyState: get(state.user.companyState, "state", ""),
    groups: state.employer.groups,
    numberOfPendingInvites: unacceptedInvites.length,
    numberOfLinkRequests: pendingLinks.length,
    invalidBankIdsLinkedToGroups: invalidBankIdsLinkedToGroupsSelector(state),
    company,
    // payroll slice is shallow-typed (totalAmount is `unknown`); cast at the
    // boundary to the `number` TotalsTable expects — preserved from the JS.
    totalAmount: state.payroll.totalAmounts.totalAmount as number,
    linkedEmployees: totalPlanParticipants,
    error: errorSelector(state),
    hasBank: isBankVerifiedSelector(state),
    hasGroup: !isEmpty(state.employer.groups),
    reAuthRequired,
    bankAccounts: banksSelector(state),
    hasMultipleBanksPendingReAuth,
    banksNeedingReAuth,
    reAuthBank,
    isEmployer: isEmployerSelector(state),
    employerReAuthRequired: reAuthRequired,
    payrollIntegrationStatus: getPayrollIntegrationStatus(state),
  };
};

const mapDispatchToProps = {
  push,
  setTab,
  hydrateDashboard,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Externally-passed handlers preserved from the original PropTypes surface.
  // Only `onSuccess` is read in this component; the rest are part of the
  // declared public prop contract and kept optional.
  groupCreationHide?: (...args: unknown[]) => void;
  show?: (...args: unknown[]) => void;
  onCancel?: (...args: unknown[]) => void;
  onSuccess?: (token: string, metadata: unknown, userLegalName: string) => void;
  onClick?: (...args: unknown[]) => void;
  updateExpiringLink?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
}

interface DashboardContent {
  content: React.ReactNode;
  subtext: string | null;
  title?: string;
}

class EmployerDashboard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
    };
  }

  componentDidMount() {
    void this.props
      .hydrateDashboard(this.props.client as ApolloClient<object>)
      .then(() => {
        this.setState({ initialFetching: false });
      });
  }

  _handleOnSuccess = (
    token: string,
    metadata: unknown,
    userLegalName: string
  ) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(token, metadata, userLegalName);
      toast.success("Bank account successfully re-authenticated.");
    }
  };

  _getPlaidLinkUpdateCard = (
    isExpiredBank: UserBank | null
  ): React.ReactNode => {
    if (this.props.hasMultipleBanksPendingReAuth) {
      return (
        <Button
          name="submit"
          color="action"
          size="sm"
          onClick={() => {
            this.props.push("/dashboard/company/bank");
          }}
          btnLabel="Authenticate Banks"
          withArrow
        />
      );
    } else if (this.props.employerReAuthRequired) {
      return (
        <ReAuthPlaidLink
          bank={isExpiredBank}
          // @ts-expect-error isEmployer is supplied by ReAuthPlaidLink's own mapStateToProps; this parent-passed value is inert (store wins) — preserved from the original JS.
          isEmployer={this.props.isEmployer}
          onSuccess={this._handleOnSuccess}
        />
      );
    }
  };

  _getFullOnboardedEmployerDashboard = () => {
    const { reAuthBank } = this.props;

    const bankAccountName = reAuthBank ? reAuthBank.bankName : null;
    const lastFourDigits = reAuthBank ? reAuthBank.accountId : null;

    const title = this.props.hasMultipleBanksPendingReAuth
      ? "Authentication required for multiple bank accounts."
      : "Bank Authentication Required!";
    const subheader = this.props.hasMultipleBanksPendingReAuth
      ? "Re-authenticate your bank accounts to resume payroll deductions."
      : `Re-authenticate your ${bankAccountName} bank account ending in ${lastFourDigits} to resume payroll deductions.`;

    return (
      <>
        {this.props.payrollIntegrationStatus ===
          PendingDebitAuthorizationAcknowledgment && (
          <Row>
            <Col md={12}>
              <Card
                className="ach-acknowledgment-card"
                style={{
                  borderLeft: "4px solid #1d99a9",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <Card.Body style={{ padding: "24px 30px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#e8f4f6",
                        borderRadius: "50%",
                        padding: "10px",
                        marginRight: "12px",
                      }}
                    >
                      <FiAlertTriangle color="#1d99a9" size={20} />
                    </div>
                    <h5
                      style={{
                        margin: 0,
                        fontWeight: 600,
                        color: "#3f4659",
                      }}
                    >
                      Acknowledgment of recurring debits
                    </h5>
                  </div>
                  <PayrollIntegratedACHDisclaimer />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            {this.props.employerReAuthRequired && (
              <IconBanner
                icon={
                  <FiAlertTriangle color="white" stroke="#B12121" size={16} />
                }
                color="error"
                title={title}
                subheader={subheader}
                actionComponent={this._getPlaidLinkUpdateCard(reAuthBank)}
              />
            )}
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TotalsTable
              numOfPendingApprovals={this.props.numberOfLinkRequests}
              totalAmount={this.props.totalAmount}
              linkedEmployees={this.props.linkedEmployees}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <EmployeePlanParticipation />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <CurrentPayrollTable
              // @ts-expect-error groups is supplied by CurrentPayrollTable's own mapStateToProps; this parent-passed value is inert (store wins) — preserved from the original JS.
              groups={this.props.groups}
            />
          </Col>
        </Row>
      </>
    );
  };

  _getOutdatedGroupsDashboard = () => {
    return (
      <>
        <Row>
          <Col md={6}>
            <UpdateGroupCard
              numOfInvalidBanks={this.props.invalidBankIdsLinkedToGroups.length}
            />
          </Col>
        </Row>
      </>
    );
  };

  _getAllBanksRemovedDashboard = () => {
    return (
      <>
        <Row>
          <Col md={6}>
            <AddBankCard />
          </Col>
        </Row>
      </>
    );
  };

  _getContent = (): DashboardContent => {
    let content: React.ReactNode = null;
    let subtext: string | null = null;
    let title: string | undefined;

    if (this.state.initialFetching) {
      content = <IconSpinner centered />;
      return { content, subtext, title };
    }
    const verifiedOrPendingBankExists = some(
      this.props.bankAccounts,
      (account) => isBankVerified(account) || isBankPending(account)
    );

    const removedAllBankAccounts =
      !verifiedOrPendingBankExists && this.props.hasGroup;

    if (removedAllBankAccounts) {
      title = "Add a Bank Account";
      subtext =
        "It looks like there are no bank accounts linked to your account. Let's link your bank account again " +
        "before updating your groups!";
      content = this._getAllBanksRemovedDashboard();
    } else if (this.props.invalidBankIdsLinkedToGroups.length > 0) {
      title = "Update Your Groups";
      subtext =
        "There are some groups with an invalid bank attached. Update your groups with a new bank to" +
        " start processing contributions again!";
      content = this._getOutdatedGroupsDashboard();
    } else {
      content = this._getFullOnboardedEmployerDashboard();
      subtext = "Here’s up to date information on your plan.";
      // company.name is `unknown` on the EmployerState record; cast to the
      // string PageHeading expects (no runtime change).
      title = this.props.company.name as string;
    }

    return { content, subtext, title };
  };

  render() {
    const { content, subtext, title } = this._getContent();
    return (
      <div>
        <PageHeading
          title={title}
          // subtext is string | null here (initialized null, like the original);
          // PageHeading renders it via `{subtext && ...}`, so null is inert.
          // Cast preserves the exact value passed — no `?? undefined` coercion.
          subtext={subtext as string | undefined}
        />
        <div className="main-content">{content}</div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerDashboard));
